import type { InvoiceLine, PriceMode, VatRateCode } from './api';

// Integer arithmetic keeps the preview aligned with the server's HALF_UP money rounding.
function decimal(value: string): { digits: bigint; scale: bigint } | null {
  const normalized = value.trim().replace(/\s/g, '').replace(',', '.');
  if (!/^-?\d+(?:\.\d+)?$/.test(normalized)) return null;
  const [whole, fraction = ''] = normalized.split('.');
  return { digits: BigInt(whole + fraction), scale: 10n ** BigInt(fraction.length) };
}

function rounded(numerator: bigint, denominator: bigint): bigint {
  if (numerator < 0n) return -rounded(-numerator, denominator);
  return (numerator * 2n + denominator) / (denominator * 2n);
}

function rateOf(rate: VatRateCode): bigint {
  return /^\d+$/.test(rate) ? BigInt(rate) : 0n;
}

function cents(value: string): bigint | null {
  const parsed = decimal(value);
  return parsed ? rounded(parsed.digits * 100n, parsed.scale) : null;
}

function stringOfCents(value: bigint): string {
  const absolute = value < 0n ? -value : value;
  return `${value < 0n ? '-' : ''}${absolute / 100n}.${String(absolute % 100n).padStart(2, '0')}`;
}

export function normalizeUnitPrice(price: string): string {
  const value = cents(price);
  return value === null ? price : stringOfCents(value);
}

export function convertUnitPrice(price: string, rate: VatRateCode, from: PriceMode, to: PriceMode): string {
  const value = cents(price);
  if (value === null || from === to) return price;
  const percent = rateOf(rate);
  const converted = from === 'NET'
    ? rounded(value * (100n + percent), 100n)
    : rounded(value * 100n, 100n + percent);
  return stringOfCents(converted);
}

export type LinePreview = { net: number; vat: number; gross: number };

export function previewLines(lines: InvoiceLine[], mode: PriceMode): LinePreview[] {
  const amounts = lines.map((line) => {
    const quantity = decimal(line.quantity);
    const price = decimal(line.unitPrice);
    return quantity && price
      ? rounded(quantity.digits * price.digits * 100n, quantity.scale * price.scale)
      : 0n;
  });
  const vat = amounts.map(() => 0n);
  const groups = new Map<VatRateCode, number[]>();
  lines.forEach((line, index) => groups.set(line.vatRate, [...(groups.get(line.vatRate) ?? []), index]));
  for (const [rate, indexes] of groups) {
    const percent = rateOf(rate);
    let cumulativeAmount = 0n;
    let assigned = 0n;
    indexes.forEach((index) => {
      cumulativeAmount += amounts[index];
      const cumulativeVat = rounded(cumulativeAmount * percent, mode === 'NET' ? 100n : 100n + percent);
      vat[index] = cumulativeVat - assigned;
      assigned = cumulativeVat;
    });
  }
  return amounts.map((value, index) => ({
    net: Number(mode === 'NET' ? value : value - vat[index]) / 100,
    vat: Number(vat[index]) / 100,
    gross: Number(mode === 'GROSS' ? value : value + vat[index]) / 100,
  }));
}
